import { UserStatus } from 'module/api/user/types';
import { fullName } from 'module/helpers/full-name';
import { getRandomColor } from 'module/helpers/get-random-color';
import { SingleUserField } from 'module/types/filter-field/field/filterable-field';
import { NoValue } from 'module/ui/components/atoms/no-value/empty-cell';
import { renderFieldEditorUserAvatar } from 'module/ui/components/molecules/field-editors/components/field-editor-user-avatar';
import { Select, Tag, type Color, type UserOption } from '@rocketlane/ui';
import { forwardRef } from 'react';
import { EditableProps } from './editable-props';

interface Props extends EditableProps<number> {
  field: SingleUserField;
}

interface EditableUserOption extends UserOption {
  value: number;
  userStatus?: UserStatus;
}

const toPackageColor = (color: ReturnType<typeof getRandomColor>): Color =>
  color === 'white' ? 'outline' : color;

export const SingleUserFieldValue = forwardRef<HTMLDivElement, Props>(
  ({ field, editable, onChange }, ref) => {
    const { users, options } = field.metaData;

    const selected_user = users.by_user_id[field.value!];
    const selectedOption: EditableUserOption | null =
      selected_user == null
        ? null
        : {
            label: fullName(selected_user),
            description: selected_user.role?.roleName,
            imageUrl: selected_user.photoUrl,
            userStatus: selected_user.userStatus,
            value: selected_user.userId,
          };

    return !editable ? (
      selected_user == null ? (
        <NoValue />
      ) : (
        <Tag
          avatar={
            selectedOption
              ? renderFieldEditorUserAvatar(selectedOption)
              : undefined
          }
          color={toPackageColor(getRandomColor(selected_user.userId))}
          data-cy={`tag-value-${fullName(selected_user)}`}
          variant="basic"
        >
          {fullName(selected_user)}
        </Tag>
      )
    ) : (
      <Select<EditableUserOption>
        ref={ref}
        variant="user"
        virtualized
        renderOptionIcon={renderFieldEditorUserAvatar}
        isClearable
        menuIsOpen={false}
        options={options}
        value={selectedOption}
        onChange={(latest) => onChange?.(latest?.value)}
      />
    );
  },
);

SingleUserFieldValue.displayName = 'SingleUserFieldValue';
